import { notification } from 'antd';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { teachersApi } from '../../../api/teachers/teachersApi';
import { Avatar } from '../../../components/Avatar/Avatar';
import { dateStringToYYYYMMDD } from '../../../utils/functions';
import moment from 'moment';
import { documentsApi, filesApi } from '@/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // Added import
import { DOCUMENT_TYPE } from '@/utils/constants';

const Edit = () => {
    const { t } = useTranslation(); // Added hook
    const [searchParams, setSearchParams] = useSearchParams();
    const { handleSubmit, control, reset, getValues } = useForm();
    const id = searchParams.get('id');
    const [isLoadingTeacherDetail, setIsLoadingTeacherDetail] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');
    const [uploadedFile, setUploadedFile] = useState();
    const fetchTeacherDetail = () => {
        if (!id) return;
        setIsLoadingTeacherDetail(true);
        teachersApi
            .getDetail(id)
            .then((res) => {
                const data = res.data.data;
                setAvatarUrl(data?.avatar);
                reset({
                    firstName: data?.firstName,
                    lastName: data?.lastName,
                    phone: data?.phone,
                    email: data?.email,
                    dateOfBirth: data?.dateOfBirth ? moment(data?.dateOfBirth).toDate() : null,
                    gender: data?.gender,
                    subject: data.subject,
                    major: data.major,
                    experience: data.experience,
                    onboardingDate: data.onboardingDate
                        ? moment(data.onboardingDate).toDate()
                        : null,
                    bio: data?.bio
                });
            })
            .catch((err) => {
                console.error('Fetch teacher detail error:', err);
                notification.error({
                    message: t('Teacher_Admin_Edit_Load_Failed_Message'),
                    description:
                        err.response?.data?.message ||
                        t('Teacher_Admin_Edit_Load_Failed_Description')
                });
            })
            .finally(() => {
                setIsLoadingTeacherDetail(false);
            });
    };

    useEffect(() => {
        if (id) {
            fetchTeacherDetail();
        } else {
            reset({
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                dateOfBirth: null,
                gender: 'Male',
                subject: '',
                major: '',
                experience: '',
                onboardingDate: null,
                password: '',
                confirmPassword: '',
                bio: ''
            });
        }
    }, [id]);

    const handleAvatarChange = (file) => {
        if (file instanceof File) {
            filesApi
                .upload({
                    fileName: file.name,
                    fileType: file.type,
                    entityName: 'teachers'
                })
                .then((res) => {
                    notification.info({
                        message: t('Teacher_Admin_Edit_Avatar_Uploading_Message')
                    });
                    return axios.put(res?.data?.data?.url, file, {
                        headers: {
                            'Content-Type': file.type
                        }
                    });
                })
                .then(() => {
                    filesApi
                        .view({
                            fileName: file.name,
                            entityName: 'teachers'
                        })
                        .then((res) => {
                            setAvatarUrl(res?.data?.data?.url);
                            notification.success({
                                message: t('Teacher_Admin_Edit_Avatar_Success_Message')
                            });
                        });

                    setUploadedFile(file);
                })
                .catch((err) => {
                    console.error('Avatar upload error:', err);
                    notification.error({
                        message: t('Teacher_Admin_Edit_Avatar_Failed_Message'),
                        description:
                            err.response?.data?.message ||
                            t('Teacher_Admin_Edit_Avatar_Failed_Description')
                    });
                });
        }
    };

    const submitForm = (formPayload) => {
        setIsLoadingTeacherDetail(true);

        const { confirmPassword, ...apiPayload } = formPayload;

        const payload = {
            ...apiPayload,
            dateOfBirth: apiPayload.dateOfBirth
                ? dateStringToYYYYMMDD(apiPayload.dateOfBirth)
                : null,
            onboardingDate: apiPayload.onboardingDate
                ? dateStringToYYYYMMDD(apiPayload.onboardingDate)
                : null
            // avatar: apiPayload.avatar || null
        };

        const apiCall = id
            ? teachersApi.update({ ...payload, id: id })
            : teachersApi.create(payload);

        const successMessage = id
            ? t('Teacher_Admin_Edit_Submit_UpdateSuccess_Message')
            : t('Teacher_Admin_Edit_Submit_CreateSuccess_Message');
        const failMessage = id
            ? t('Teacher_Admin_Edit_Submit_UpdateFailed_Message')
            : t('Teacher_Admin_Edit_Submit_CreateFailed_Message');

        apiCall
            .then((res) => {
                notification.success({ message: successMessage });
                if (uploadedFile) {
                    documentsApi.create({
                        name: 'Teacher Avatar',
                        fileType: uploadedFile.type,
                        documentType: DOCUMENT_TYPE.AVATAR,
                        fileName: uploadedFile.name,
                        entityName: 'teachers',
                        entityId: id ? id : res?.data?.data?.id
                    });
                }
                if (id) {
                    fetchTeacherDetail();
                } else {
                    reset({
                        firstName: '',
                        lastName: '',
                        phone: '',
                        email: '',
                        dateOfBirth: null,
                        gender: 'Male',
                        subject: '',
                        major: '',
                        experience: '',
                        onboardingDate: null,
                        password: '',
                        confirmPassword: '',
                        bio: ''
                    });
                }
            })
            .catch((err) => {
                notification.error({
                    message: failMessage,
                    description:
                        err.response?.data?.message ||
                        t('Teacher_Admin_Edit_Submit_Failed_Description')
                });
                console.error(err);
            })
            .finally(() => {
                setIsLoadingTeacherDetail(false);
            });
    };

    const onFormError = (errors) => {
        console.error('Form validation errors:', errors);
        if (errors.confirmPassword && errors.confirmPassword.type === 'validate') {
            if (Object.keys(errors).length === 1) {
                return;
            }
        }

        notification.warning({
            message: t('Teacher_Admin_Edit_Validation_MissingFields_Message'),
            description: t('Teacher_Admin_Edit_Validation_MissingFields_Description')
        });
    };

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="header-action">
                            <h1 className="page-title">
                                {id
                                    ? t('Teacher_Admin_Edit_PageTitle_Edit')
                                    : t('Teacher_Admin_Edit_PageTitle_Create')}
                            </h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="#">{t('Teacher_Admin_Edit_Breadcrumb_Brand')}</a>
                                </li>
                                <li className="breadcrumb-item">
                                    <a href="/teachers">
                                        {t('Teacher_Admin_Edit_Breadcrumb_Teachers')}
                                    </a>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {id
                                        ? t('Teacher_Admin_Edit_Breadcrumb_Active_Edit')
                                        : t('Teacher_Admin_Edit_Breadcrumb_Active_Create')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="section-body mt-4">
                <div className="row clearfix">
                    {isLoadingTeacherDetail && id ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                minHeight: '300px'
                            }}>
                            <Spinner style={{ width: '3rem', height: '3rem' }} />
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(submitForm, onFormError)}
                            className="flex flex-wrap">
                            {' '}
                            <div className="col-lg-8 col-md-12 col-sm-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {t('Teacher_Admin_Edit_CardTitle_BasicInfo')}
                                        </h3>
                                    </div>
                                    <div className="mx-auto flex flex-col items-center justify-center pt-3 pb-3">
                                        {' '}
                                        <label className="mb-2">
                                            {t('Teacher_Admin_Edit_Form_Avatar')}
                                        </label>
                                        <Avatar
                                            value={avatarUrl}
                                            onChange={(file) => handleAvatarChange(file)}
                                            size={100}
                                        />
                                    </div>
                                    <div className="card-body">
                                        <div className="row clearfix">
                                            <div className="col-md-6 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_FirstName')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_FirstName_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="firstName"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_LastName')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_LastName_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="lastName"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Phone')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="tel"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_Phone_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="phone"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Email')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{
                                                            required: true,
                                                            pattern: /^\S+@\S+$/i
                                                        }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="email"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_Email_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="email"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_DOB')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="dateOfBirth"
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                dateFormat="dd-MM-yyyy"
                                                                className="form-control w-full"
                                                                selected={field.value}
                                                                placeholderText={t(
                                                                    'Teacher_Admin_Edit_Form_Date_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Gender')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="gender"
                                                        defaultValue={'Male'}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <select
                                                                {...field}
                                                                className="form-control show-tick">
                                                                <option value="Male">
                                                                    {t(
                                                                        'Teacher_Admin_Edit_Form_Gender_Male'
                                                                    )}
                                                                </option>
                                                                <option value="Female">
                                                                    {t(
                                                                        'Teacher_Admin_Edit_Form_Gender_Female'
                                                                    )}
                                                                </option>
                                                            </select>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <label>
                                                    {t('Teacher_Admin_Edit_Form_Subject')}{' '}
                                                    <span style={{ color: 'red' }}>*</span>
                                                </label>
                                                <Controller
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <input
                                                            {...field}
                                                            type="text"
                                                            className="form-control"
                                                            placeholder={t(
                                                                'Teacher_Admin_Edit_Form_Subject_Placeholder'
                                                            )}
                                                        />
                                                    )}
                                                    name="subject"
                                                />
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Major')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_Major_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="major"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Experience')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_Experience_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="experience"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t(
                                                            'Teacher_Admin_Edit_Form_OnboardingDate'
                                                        )}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="onboardingDate"
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <DatePicker
                                                                {...field}
                                                                dateFormat="dd-MM-yyyy"
                                                                className="form-control w-full"
                                                                selected={field.value}
                                                                placeholderText={t(
                                                                    'Teacher_Admin_Edit_Form_Date_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-12 col-sm-12">
                                                <div className="form-group">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Bio')}{' '}
                                                        <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <textarea
                                                                {...field}
                                                                className="form-control"
                                                                rows="4"
                                                                placeholder={t(
                                                                    'Teacher_Admin_Edit_Form_Bio_Placeholder'
                                                                )}
                                                            />
                                                        )}
                                                        name="bio"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4 col-md-12 col-sm-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {t('Teacher_Admin_Edit_CardTitle_AccountInfo')}
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row clearfix">
                                            <div className="col-md-6 col-sm-12">
                                                <div className="form-group position-relative">
                                                    <label>
                                                        {t('Teacher_Admin_Edit_Form_Password')}{' '}
                                                        {!id && (
                                                            <span style={{ color: 'red' }}>*</span>
                                                        )}
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="password"
                                                        rules={{ required: !id }}
                                                        render={({ field }) => {
                                                            return (
                                                                <div className="position-relative">
                                                                    <input
                                                                        {...field}
                                                                        type={
                                                                            showPassword
                                                                                ? 'text'
                                                                                : 'password'
                                                                        }
                                                                        className="form-control"
                                                                        placeholder={
                                                                            id
                                                                                ? t(
                                                                                      'Teacher_Admin_Edit_Form_Password_Placeholder_Edit'
                                                                                  )
                                                                                : t(
                                                                                      'Teacher_Admin_Edit_Form_Password_Placeholder_Create'
                                                                                  )
                                                                        }
                                                                    />
                                                                    <i
                                                                        className={`fa ${
                                                                            showPassword
                                                                                ? 'fa-eye-slash'
                                                                                : 'fa-eye'
                                                                        }`}
                                                                        onClick={() =>
                                                                            setShowPassword(
                                                                                !showPassword
                                                                            )
                                                                        }
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            right: '10px',
                                                                            transform:
                                                                                'translateY(-50%)',
                                                                            cursor: 'pointer',
                                                                            color: '#6c757d'
                                                                        }}></i>
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-6 col-sm-12">
                                                <div className="form-group position-relative">
                                                    <label>
                                                        {t(
                                                            'Teacher_Admin_Edit_Form_ConfirmPassword'
                                                        )}{' '}
                                                        {!id && (
                                                            <span style={{ color: 'red' }}>*</span>
                                                        )}
                                                    </label>
                                                    <Controller
                                                        control={control}
                                                        name="confirmPassword"
                                                        rules={{
                                                            required: !id,
                                                            validate: (value) =>
                                                                value === getValues('password') ||
                                                                t(
                                                                    'Teacher_Admin_Edit_Form_ConfirmPassword_Validation'
                                                                )
                                                        }}
                                                        render={({
                                                            field,
                                                            fieldState: { error }
                                                        }) => {
                                                            return (
                                                                <div className="position-relative">
                                                                    <input
                                                                        {...field}
                                                                        type={
                                                                            showConfirmPassword
                                                                                ? 'text'
                                                                                : 'password'
                                                                        }
                                                                        className={`form-control ${
                                                                            error
                                                                                ? 'is-invalid'
                                                                                : ''
                                                                        }`}
                                                                        placeholder={t(
                                                                            'Teacher_Admin_Edit_Form_ConfirmPassword_Placeholder'
                                                                        )}
                                                                    />
                                                                    <i
                                                                        className={`fa ${
                                                                            showConfirmPassword
                                                                                ? 'fa-eye-slash'
                                                                                : 'fa-eye'
                                                                        }`}
                                                                        onClick={() =>
                                                                            setShowConfirmPassword(
                                                                                !showConfirmPassword
                                                                            )
                                                                        }
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            right: '10px',
                                                                            transform:
                                                                                'translateY(-50%)',
                                                                            cursor: 'pointer',
                                                                            color: '#6c757d'
                                                                        }}></i>
                                                                    {error && (
                                                                        <div className="invalid-feedback">
                                                                            {error.message}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12 mt-3">
                                    {' '}
                                    <button
                                        type="submit"
                                        className="mr-1 btn btn-primary"
                                        disabled={isLoadingTeacherDetail}>
                                        {isLoadingTeacherDetail
                                            ? t('Teacher_Admin_Edit_Button_Saving')
                                            : t('Teacher_Admin_Edit_Button_Submit')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => window.history.back()}
                                        disabled={isLoadingTeacherDetail}>
                                        {t('Teacher_Admin_Edit_Button_Cancel')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default Edit;
